import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import useConfig from '../../config/state/use-config'

export interface MParticleEventProperties {
  // Organization properties
  npo_name: string
  npo_id: string

  // Campaign properties
  campaign_id: string

  // Donation components
  donation_components: {
    donation_frequency_default: string
    donation_amount_default: number
    donation_currency_default: string
    cover_transaction_fees_default: boolean
    cover_transaction_fees_checkbox: boolean
    monthly_support_nudge: boolean
    transaction_fees_nudge: boolean
    donation_designation: boolean
    employer_matching: boolean
    donation_comment: boolean
    donation_dedication: boolean
    donation_dedication_email: boolean
    donation_dedication_letter: boolean
    custom_question: number
    optional_donor_info: boolean
    total_steps: number
  }

  // Donation selections
  donation_selections: {
    donation_frequency: string
    donation_amount: number
    donation_currency: string
    cover_transaction_fees_accepted: boolean
    monthly_support_nudge_accepted: boolean
    transaction_fees_nudge_accepted: boolean
    donation_designation_added?: string
    employer_matching_added?: number
    donation_comment_added: boolean
    donation_dedication_added: boolean
    donation_dedication_email_added: boolean
    donation_dedication_letter_added: boolean
    custom_question_answer_added: number
    optional_donor_info_added: boolean
  }
}

/**
 * Hook that provides comprehensive event properties for mParticle events.
 * Includes organization, campaign, donation components, and donation selections data.
 *
 * @returns {MParticleEventProperties} Object containing comprehensive global event properties
 */
export const useMParticleEventProperties = (): MParticleEventProperties => {
  const config = useConfig()
  const urlParams = useSelector((state: RootState) => state.urlParams)
  const order = useSelector((state: RootState) => state.order)
  const flow = useSelector((state: RootState) => state.flow)

  return useMemo(() => {
    // Get enabled steps count
    const enabledSteps = Object.values(flow.stepsMap).filter((step) => step.enabled)
    const totalSteps = enabledSteps.length

    // Analyze custom questions
    const customQuestions = config.questions?.custom || []

    const customAnswers = Object.values(order.customAnswers || {}).filter(
      (answer) => answer !== undefined && answer !== '',
    )

    // Check for optional donor info (contact answers)
    const contactAnswersValues = Object.values(order.contactAnswers || {}).filter(
      (value) => value !== undefined && value !== '',
    )

    const hasOptionalDonorInfo = contactAnswersValues.length > 0

    // Check if there are optional custom questions answered
    const hasOptionalCustomQuestions = customQuestions.some(
      (question) => !question.is_required && order.customAnswers?.[question.id] !== undefined,
    )

    // Get default frequency from config
    const defaultFrequency =
      config.frequency?.options?.find((option) => option.isDefault)?.name || 'one-time'

    // Get default amount from config or URL params
    // Find default amount from presets for the current frequency
    const currentFrequency = order.frequency?.value || defaultFrequency

    const defaultAmountFromPresets =
      config.amount?.presets?.[currentFrequency]?.find((preset) => preset.isDefault)?.amount || 0

    const defaultAmount = urlParams.amount || defaultAmountFromPresets || 0

    // Get default currency from config or URL params
    const defaultCurrency = urlParams.currency || config.currency?.default || 'USD'

    return {
      // Organization properties
      npo_name: config.organization?.name || '',
      npo_id: config.organizationId || '',

      // Campaign properties
      campaign_id: config.campaignId || '',

      // Donation components
      donation_components: {
        donation_frequency_default: defaultFrequency,
        donation_amount_default: Number(defaultAmount) || 0,
        donation_currency_default: defaultCurrency,
        cover_transaction_fees_default: config.dcf?.checkedByDefault || false,
        cover_transaction_fees_checkbox: config.dcf?.isEnabled || false,
        monthly_support_nudge: config.recurringNudge?.enabled || false,
        transaction_fees_nudge: config.dcfNudge?.enabled || false,
        donation_designation: !config.designation?.isOptional || false,
        employer_matching: config.doubleTheDonation?.isEnabled || false,
        donation_comment: config.comment?.isEnabled || false,
        donation_dedication: config.dedication?.isEnabled || false,
        donation_dedication_email: config.dedication?.isECardEnabled || false,
        donation_dedication_letter: config.dedication?.isPrintedCardEnabled || false,
        custom_question: customQuestions.length || 0,
        optional_donor_info: customQuestions.some((question) => !question.is_required) || false,
        total_steps: totalSteps,
      },

      // Donation selections
      donation_selections: {
        donation_frequency: order.frequency?.value || 'one-time',
        donation_amount: order.amount?.value || Number(defaultAmount) || 0,
        donation_currency: order.currency?.value || defaultCurrency,
        cover_transaction_fees_accepted: order.dcf?.value?.isChecked || false,
        monthly_support_nudge_accepted: order.nudgeTracking?.recurringNudge?.wasAccepted === true,
        transaction_fees_nudge_accepted: order.nudgeTracking?.dcfNudge?.wasAccepted === true,
        donation_designation_added: order.designation?.value?.designationId
          ? String(order.designation.value.designationId)
          : undefined,
        employer_matching_added: order.doubleTheDonation?.value?.companyId,
        donation_comment_added: !!order.comment?.value,
        donation_dedication_added: !!order.dedication?.hasDedication,
        donation_dedication_email_added: !!order.dedication?.recipientEmailAddress,
        donation_dedication_letter_added: !!order.dedication?.ecardMessage,
        custom_question_answer_added: customAnswers.length || 0,
        optional_donor_info_added: hasOptionalDonorInfo || hasOptionalCustomQuestions,
      },
    }
  }, [config, urlParams, order, flow])
}

export default useMParticleEventProperties
